import { useEffect, useRef } from 'react';
import './App.scss';
import { useAtom, Provider } from 'jotai/index';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './navigation';
import MyModal from './components/MyModal';
import useTranslations from './hooks/useTranslations';
import LocalStorageServices from './services/LocalStorageServices';
import useWindowResize from './hooks/useWindowResize';
import { isAdminAtom, profileAtom, tokenAtom } from './atoms/accountAtoms';
import store from './atoms';
import sendRequest from './helpers/sendRequest';
import Api from './api';
import {
  categoriesAtom, materialsAtom, materialsWithPathAtom, materialTypesAtom,
} from './atoms/categoriesAtoms';
import {
  defaultBlockReasonAtom,
  initLoadingAtom, initVersionAtom, isInitAtom, isServerBrokenAtom, languagesListAtom, pathnameAtom, resourcePathAtom, siteModulesAtom,
} from './atoms/globalAtoms';
import {
  configuratorAnglesAtom,
  configuratorCitiesAtom,
  configuratorGlassTypesAtom,
  configuratorMaxAnglesCountAtom,
  configuratorRegionsAtom,
  configuratorSelectedAnglesAtom,
  configuratorCalculationsAtom,
  configuratorSelectedCityAtom,
  configuratorSingleConfigurationAtom,
  configuratorTemplatesAtom,
  reloadConfigurationAfterInitAtom,
} from './atoms/configuratorAtoms';
import ConfiguratorServices from './services/ConfiguratorServices';
import {
  closeModal2Atom,
  closeModalAtom, modal2StateAtom, modalStateAtom, openLoadingModalAtom,
} from './atoms/modalAtoms';
import { configurationsItemsOnPageAtom } from './atoms/configurationsAtoms';
import { projectsItemsOnPageAtom } from './atoms/projectsAtoms';
import { feedbackTopicsAtoms } from './atoms/feedbackAtoms';
import LanguagesServicesService from './services/LanguagesServices';
import Tutorials from './pages/Tutorials';
import useTutorials from './hooks/useTutorials';
import Head from './components/Head';

function App() {
  useWindowResize();
  const { lang, setLang } = useTranslations();
  const { handleTutorialStage } = useTutorials();

  const [token, setToken] = useAtom(tokenAtom);
  const [profile, setProfile] = useAtom(profileAtom);
  const [pathname] = useAtom(pathnameAtom);
  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, setIsServerBroken] = useAtom(isServerBrokenAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [, setIsAdmin] = useAtom(isAdminAtom);
  const [, setLanguagesList] = useAtom(languagesListAtom);
  const [, setCategories] = useAtom(categoriesAtom);
  const [, setMaterialsWithPath] = useAtom(materialsWithPathAtom);
  const [, setMaterialTypes] = useAtom(materialTypesAtom);
  const [, setMaterials] = useAtom(materialsAtom);
  const [, setConfiguratorCities] = useAtom(configuratorCitiesAtom);
  const [, setConfiguratorSelectedCity] = useAtom(configuratorSelectedCityAtom);
  const [, setConfiguratorSelectedAngles] = useAtom(configuratorSelectedAnglesAtom);
  const [configuratorSingleConfiguration, setConfiguratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);
  const [configuratorCalculations] = useAtom(configuratorCalculationsAtom);
  const [, setReloadConfigurationAfterInit] = useAtom(reloadConfigurationAfterInitAtom);
  const langRef = useRef(lang);
  const [, setConfiguratorRegions] = useAtom(configuratorRegionsAtom);
  const [, setConfiguratorTemplates] = useAtom(configuratorTemplatesAtom);
  const [, setConfiguratorGlassTypes] = useAtom(configuratorGlassTypesAtom);
  const [, setConfiguratorAngles] = useAtom(configuratorAnglesAtom);
  const [, setConfiguratorMaxAnglesCount] = useAtom(configuratorMaxAnglesCountAtom);
  const [, setProjectsItemsOnPage] = useAtom(projectsItemsOnPageAtom);
  const [, setConfigurationsItemsOnPage] = useAtom(configurationsItemsOnPageAtom);
  const [, setFeedbackTopics] = useAtom(feedbackTopicsAtoms);
  const [, setSiteModules] = useAtom(siteModulesAtom);
  const [, setResourcePath] = useAtom(resourcePathAtom);
  const [, setDefaultBlockReason] = useAtom(defaultBlockReasonAtom);

  const [, setInitLoading] = useAtom(initLoadingAtom);
  const [isInit, setIsInit] = useAtom(isInitAtom);
  const [, setInitVersion] = useAtom(initVersionAtom);

  useEffect(() => {
    setToken(LocalStorageServices.getToken());
  }, []);

  useEffect(() => {
    const langChanged = langRef.current !== lang;
    langRef.current = lang;

    if (token) {
      // Init stores the language. The configuration is requested only after that, so its texts match the new language.
      const params = new URLSearchParams(window.location.search);
      const onlyDetails = params.get('only_details') === 'true';
      const configurationId = params.get('configuration_id') || configuratorSingleConfiguration?.id;
      const hasOpenResults = Boolean(configuratorCalculations) || onlyDetails;
      const reloadConfigurationId = langChanged && pathname === '/configurator' && hasOpenResults
        ? configurationId
        : null;

      openLoadingModal();
      getProfile().then();
      init({ lang, reloadConfigurationId }).then(() => {
        closeModal();
      });
    } else {
      initSite({ lang }).then(() => {});
    }
  }, [token, lang]);

  const getProfile = async () => {
    const data = await sendRequest({
      request: Api.getProfile,
      warnErrorText: 'while getting profile',
    });

    if (data?.status === 'ok') {
      setProfile(data.data.user);
    }
  };

  const init = async ({ lang: langLocal, reloadConfigurationId }) => {
    const data = await sendRequest({
      request: Api.init,
      payload: { lang: langLocal },
      warnErrorText: 'while init',
      showErrorInModal: false,
      setLoading: setInitLoading,
    });

    if (data?.status === 'ok' && data?.data) {
      onInitSuccess();
      setIsAdmin(data.data.is_admin);
      setLanguagesList(LanguagesServicesService.convertLanguagesInitList(data.data.langs));
      setLang(LocalStorageServices.getLanguage());
      const injectedCategories = ConfiguratorServices.getCategoriesFullList(data.data.categories, data.data.materials);
      setCategories(injectedCategories);
      setMaterialsWithPath(ConfiguratorServices.getMaterialsWithPathList(injectedCategories));
      setMaterials(data.data.materials);
      setMaterialTypes(data.data.material_types);

      setConfiguratorRegions(data.data.areas);
      setConfiguratorCities(data.data.cities);
      setConfiguratorSelectedCity((prev) => {
        if (prev?.id != null) {
          const found = data.data.cities.find((city) => city.id === prev.id);
          if (found) return found;
        }
        return data.data.cities.find((i) => i.selected) || data.data.cities[0];
      });
      setConfiguratorSingleConfiguration((prev) => {
        if (!prev?.city_id) return prev;
        const found = data.data.cities.find((city) => city.id === prev.city_id);
        if (!found || found.name === prev.city) return prev;
        return { ...prev, city: found.name };
      });

      setConfiguratorTemplates(data.data.configuration_templates);
      setConfiguratorGlassTypes(data.data.scratch_default_glasses);
      const newAngles = data.data.angles.map((i, index) => ({ id: index, name: i.name + i.unit }));
      setConfiguratorAngles(newAngles);
      setConfiguratorSelectedAngles((prev) => {
        if (!prev?.length) return prev;
        return prev.map((angle) => {
          if (angle.id === 'SELF_VALUE') return angle;
          return newAngles.find((item) => item.id === angle.id) || angle;
        });
      });
      setConfiguratorMaxAnglesCount(data.data.max_angles_count);

      setProjectsItemsOnPage(data.data.projects_items_on_page);
      setConfigurationsItemsOnPage(data.data.configurations_items_on_page);
      setFeedbackTopics(data.data.feedback_topics);
      setSiteModules(data.data.site_modules);
      setResourcePath(data.data.resource_path);
      setDefaultBlockReason(data.data.default_block_reason);

      setIsInit(true);
      setInitVersion((prev) => prev + 1);
      if (reloadConfigurationId) setReloadConfigurationAfterInit(reloadConfigurationId);
    } else {
      onInitError();
    }
  };

  const initSite = async ({ lang: langLocal }) => {
    const data = await sendRequest({
      request: Api.initSite,
      payload: { lang: langLocal },
      warnErrorText: 'while init site',
      showErrorInModal: false,
    });

    if (data?.status === 'ok' && data?.data) {
      onInitSuccess();
      setLanguagesList(LanguagesServicesService.convertLanguagesInitList(data.data.langs));
      setFeedbackTopics(data.data.feedback_topics);
      setSiteModules(data.data.site_modules);
      setResourcePath(data.data.resource_path);
    } else {
      onInitError();
    }
  };

  const onInitError = () => {
    setIsServerBroken(true);
  };

  const onInitSuccess = () => {
    setIsServerBroken(false);
  };

  useEffect(() => {
    // TODO check if only_details !== 'true' && !configurator_id
    if (isInit && profile && pathname === '/configurator') handleTutorialStage();
  }, [isInit, profile, pathname]);

  return (
    <HelmetProvider>
      <Provider store={store}>
        <Head />
        <Navigation />
        <MyModal modalAtom={modalStateAtom} closeModalAtom={closeModalAtom} />
        <MyModal modalAtom={modal2StateAtom} closeModalAtom={closeModal2Atom} />

        <Tutorials />
      </Provider>
    </HelmetProvider>
  );
}

export default App;
